/**
 * Chat identity resolution from the mobile JWT.
 *
 * The server stamps every chat message's `senderId` (and keys presence /
 * chat membership) via `vEvents.Shared.PresenceUserIdResolver`, which resolves
 * a user to `attendee_id` when present and otherwise falls back to `user_id`.
 * Mobile tokens carry `attendee_id` (an alias of `registration_id`) only for
 * real event registrations; organiser / admin tokens ship `registration_id`
 * as `"0"` and no `attendee_id`, so they must fall through to `user_id`.
 *
 * The naive `attendee_id ?? registration_id ?? user_id ?? sub` decode that was
 * duplicated across the chat surfaces picked the literal string `"0"` for those
 * tokens (a non-null value short-circuits `??`), which never matches a real
 * `senderId`. The symptom was your own messages rendering on the received side
 * and never being recognised as "mine" (so the inbox kept pinging you for them).
 * Centralising the decode here — and ignoring empty / zero ids — keeps the
 * client in lock-step with the server's identifier.
 */

function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const segment = token.split('.')[1] ?? '';
    if (!segment) return null;
    return JSON.parse(atob(segment)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** A claim is a usable id only when it is non-empty and not the `"0"` sentinel. */
function asUsableId(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  if (text.length === 0 || text === '0') return null;
  return text;
}

/**
 * The caller's chat identifier, matching the server's
 * `PresenceUserIdResolver` / BFF `ChatUserId` precedence:
 * `attendee_id` → `registration_id` → `user_id` → `sub` → `nameid`.
 * Returns `null` when the token is missing or carries no usable id.
 */
export function resolveMyChatId(token: string | null | undefined): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return (
    asUsableId(payload['attendee_id']) ??
    asUsableId(payload['registration_id']) ??
    asUsableId(payload['user_id']) ??
    asUsableId(payload['sub']) ??
    asUsableId(payload['nameid'])
  );
}

/**
 * Whether `senderId` belongs to the caller identified by `token`. Used to
 * render a message on the "mine" side and to suppress self-notifications.
 */
export function isOwnChatSender(
  token: string | null | undefined,
  senderId: string | null | undefined
): boolean {
  const sender = senderId == null ? '' : String(senderId).trim();
  if (sender.length === 0) return false;
  const myId = resolveMyChatId(token);
  return myId != null && myId === sender;
}
